import {
  Component,
  inject,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  signal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
  AsyncValidatorFn,
  ValidatorFn,
} from "@angular/forms";
import {
  MatDialogModule,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatDividerModule } from "@angular/material/divider";
import { MatTooltip } from "@angular/material/tooltip";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { provideNativeDateAdapter } from "@angular/material/core";
import { Store } from "@ngrx/store";
import {
  Observable,
  map,
  startWith,
  Subject,
  takeUntil,
  catchError,
  debounceTime,
  first,
  of,
  switchMap,
  EMPTY,
  tap,
} from "rxjs";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";

import { selectProviderTaxonomies } from "@store/selectors/clinician-details/clinician-details.selectors";
import { ProviderTaxonomy, Provider } from "@models/provider.model";
import { ClinicianDetailsService, NpiLookupResult } from "@services/clinician-details.service";
import { emailLengthValidator } from "@directives/user-admin/email-validator.directive";
import { toSignal } from "@angular/core/rxjs-interop";
import { selectOrgId } from "@store/selectors/organization-emr-details/organization-emr-details.selector";

export interface CreateProviderModalData {
  firstName: string;
  lastName: string;
  npi: string;
  providerTaxonomyID: string;
  providerTaxonomyDescription: string;
  providerUserEmailAddress: string;
  phone: string;
  isActive: boolean;
  effectiveDate?: string | null;
  expirationDate?: string | null;
}

export interface CreateProviderModalInput {
  provider?: Provider;
  isEdit?: boolean;
}

@Component({
  selector: "app-create-provider-modal",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatDividerModule,
    MatTooltip,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    NgxMatSelectSearchModule,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: "./create-provider-modal.component.html",
  styleUrls: ["./create-provider-modal.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateProviderModalComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef = inject(
    MatDialogRef<CreateProviderModalComponent>
  );
  private readonly store = inject(Store);
  private readonly data = inject<CreateProviderModalInput>(MAT_DIALOG_DATA);
  private readonly clinicianService = inject(ClinicianDetailsService);
  private readonly destroy$ = new Subject<void>();
  private readonly orgId$ = this.store.select(selectOrgId);
  private readonly orgId = toSignal(this.orgId$);

  providerForm: FormGroup;
  hasSaveButtonBeenPressed = false;
  isEdit = false;
  modalTitle = "Create Provider";
  readonly npiLookupStatus = signal<'idle' | 'loading' | 'found' | 'not-found' | 'error'>('idle');

  taxonomies: ProviderTaxonomy[] = [];
  filteredTaxonomies: ProviderTaxonomy[] = [];
  private autoFilledFields = new Map<string, unknown>();

  constructor() {
    this.isEdit = this.data?.isEdit || false;
    this.modalTitle = this.isEdit ? "Edit Provider" : "Create Provider";

    this.providerForm = this.fb.group({
      firstName: ["", [Validators.required, Validators.maxLength(35)]],
      lastName: ["", [Validators.required, Validators.maxLength(35)]],
      npi: [
        "",
        [Validators.required, Validators.pattern(/^\d{10}$/), Validators.maxLength(24)],
        [this.npiAsyncValidator.bind(this)],
      ],
      specialty: [""],
      specialtySearch: [""],
      providerUserEmailAddress: [
        "",
        [
          Validators.required,
          Validators.pattern(
            /^[\w]{1,}[\w.+-]{0,}@[\w-]{1,}([.][a-zA-Z]{2,}|[.][\w-]{2,}[.][a-zA-Z]{2,})$/
          ),
          emailLengthValidator(),
        ],
      ],
      phone: ["", [Validators.maxLength(24)]],
      isActive: [true],
      effectiveDate: [null as Date | null],
      expirationDate: [null as Date | null, [this.endDateAfterStartValidator.bind(this) as ValidatorFn]],
    });

    if (this.isEdit && this.data?.provider) {
      this.populateFormForEdit(this.data.provider);
    }
  }

  ngOnInit(): void {
    this.store
      .select(selectProviderTaxonomies)
      .pipe(takeUntil(this.destroy$))
      .subscribe((taxonomies) => {
        this.taxonomies = taxonomies;
        this.updateFilteredTaxonomies();
      });

    this.providerForm
      .get("specialtySearch")
      ?.valueChanges.pipe(startWith(""), takeUntil(this.destroy$))
      .subscribe((searchTerm) => {
        this.filteredTaxonomies = this.filterTaxonomies(searchTerm || "");
      });

    this.providerForm
      .get("effectiveDate")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.providerForm.get("expirationDate")?.updateValueAndValidity();
      });

    this.providerForm
      .get("npi")
      ?.valueChanges.pipe(
        tap(() => this.npiLookupStatus.set('idle')),
        debounceTime(600),
        switchMap((value: string) => {
          const npi = this.sanitizeField(value);
          if (!/^\d{10}$/.test(npi)) {
            return EMPTY;
          }
          this.npiLookupStatus.set('loading');
          return this.clinicianService.lookupNpi(npi).pipe(
            catchError((err) => {
              this.npiLookupStatus.set(err?.status === 404 ? 'not-found' : 'error');
              return EMPTY;
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((result: NpiLookupResult) => {
        this.npiLookupStatus.set('found');
        if (!this.isEdit) {
          this.clearAutoFilledFields();
          this.autoFillFromNpiLookup(result);
        }
      });
  }

  private clearAutoFilledFields(): void {
    this.autoFilledFields.forEach((autoFilledValue, field) => {
      const control = this.providerForm.get(field);
      if (control?.value === autoFilledValue) {
        control?.setValue('', { emitEvent: false });
      }
    });
    this.autoFilledFields.clear();
  }

  private autoFillFromNpiLookup(result: NpiLookupResult): void {
    const firstNameControl = this.providerForm.get('firstName');
    if (result.firstName && !firstNameControl?.value) {
      firstNameControl?.setValue(result.firstName, { emitEvent: false });
      this.autoFilledFields.set('firstName', result.firstName);
    }

    const lastNameControl = this.providerForm.get('lastName');
    if (result.lastName && !lastNameControl?.value) {
      lastNameControl?.setValue(result.lastName, { emitEvent: false });
      this.autoFilledFields.set('lastName', result.lastName);
    }

    const specialtyControl = this.providerForm.get('specialty');
    if (result.primaryTaxonomyCode && (specialtyControl?.value === null || specialtyControl?.value === '')) {
      const matchingTaxonomy = this.taxonomies.find(
        (taxonomy) => taxonomy.providerTaxonomyCode === result.primaryTaxonomyCode
      );
      if (matchingTaxonomy) {
        specialtyControl?.setValue(matchingTaxonomy.id, { emitEvent: false });
        this.autoFilledFields.set('specialty', matchingTaxonomy.id);
      }
    }
  }

  private sanitizeField(value: string | null | undefined): string {
    if (!value) return '';
    return value
      .replace(/\u00A0/g, '') // CHAR(160) non-breaking space
      .replace(/\t/g, '')      // CHAR(9)   tab
      .replace(/\r/g, '')      // CHAR(13)  carriage return
      .replace(/\n/g, '')      // CHAR(10)  newline
      .trim();
  }

  onNpiPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') ?? '';
    const sanitized = this.sanitizeField(pastedText);
    const npiControl = this.providerForm.get('npi');
    const input = event.target as HTMLInputElement;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const currentValue = npiControl?.value ?? '';
    const newValue = currentValue.slice(0, start) + sanitized + currentValue.slice(end);
    npiControl?.markAsDirty();
    npiControl?.markAsTouched();
    npiControl?.setValue(newValue);
    setTimeout(() => {
      input.setSelectionRange(start + sanitized.length, start + sanitized.length);
    });
  }

  private npiAsyncValidator: AsyncValidatorFn = (
    control: AbstractControl
  ): Observable<ValidationErrors | null> => {
    const trimmedValue = this.sanitizeField(control.value);
    if (!trimmedValue || !control.dirty || !/^\d{10}$/.test(trimmedValue)) {
      return of(null);
    }
    const excludeProviderId =
      this.isEdit && this.data?.provider ? this.data.provider.id : undefined;

    return of(trimmedValue).pipe(
      debounceTime(1000),
      switchMap((npi) =>
        this.clinicianService.validateNPI(npi, parseInt(this.orgId() ?? ""), excludeProviderId)
      ),
      map((isAvailable) => {
        return isAvailable ? null : { npiDuplicate: true };
      }),
      catchError(() => of({ npiDuplicate: true })),
      first()
    );
  };

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private endDateAfterStartValidator(control: AbstractControl): ValidationErrors | null {
    const parent = control.parent;
    if (!parent) return null;
    const effectiveDate: Date | null = parent.get('effectiveDate')?.value;
    const expirationDate: Date | null = control.value;
    if (effectiveDate && expirationDate && expirationDate < effectiveDate) {
      return { endBeforeStart: true };
    }
    return null;
  }

  private parseISODate(dateString: string): Date {
    const parts = dateString.split('-');
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  }

  private formatDateToISO(date: Date | null): string | null {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private populateFormForEdit(provider: Provider): void {
    this.providerForm.patchValue({
      firstName: provider.firstName,
      lastName: provider.lastName,
      npi: provider.npi,
      specialty: provider.providerTaxonomyId,
      providerUserEmailAddress: provider.providerUserEmailAddress,
      phone: provider.phone,
      isActive: provider.active === 1,
      effectiveDate: provider.effectiveDate ? this.parseISODate(provider.effectiveDate) : null,
      expirationDate: provider.expirationDate ? this.parseISODate(provider.expirationDate) : null,
    });
  }

  onSave(): void {
    this.hasSaveButtonBeenPressed = true;
    this.markAllFieldsAsTouched();

    if (this.providerForm.valid) {
      const formValue = this.providerForm.value;
      const selectedTaxonomy = this.taxonomies.find(
        (t) => t.id === formValue.specialty
      );

      const formData: CreateProviderModalData = {
        firstName: this.sanitizeField(formValue.firstName),
        lastName: this.sanitizeField(formValue.lastName),
        npi: this.sanitizeField(formValue.npi),
        providerTaxonomyID: selectedTaxonomy?.id?.toString() || "",
        providerTaxonomyDescription:
          selectedTaxonomy?.providerTaxonomyDescription || "",
        providerUserEmailAddress: this.sanitizeField(formValue.providerUserEmailAddress),
        phone: this.sanitizeField(formValue.phone),
        isActive: formValue.isActive,
        effectiveDate: this.formatDateToISO(formValue.effectiveDate),
        expirationDate: this.formatDateToISO(formValue.expirationDate),
      };

      this.dialogRef.close(formData);
    }

    setTimeout(() => {
      this.hasSaveButtonBeenPressed = false;
    }, 3000);
  }

  onClose(): void {
    this.dialogRef.close();
  }

  private markAllFieldsAsTouched(): void {
    Object.keys(this.providerForm.controls).forEach((key) => {
      this.providerForm.get(key)?.markAsTouched();
    });
  }

  private updateFilteredTaxonomies(): void {
    const searchTerm = this.providerForm.get("specialtySearch")?.value || "";
    this.filteredTaxonomies = this.filterTaxonomies(searchTerm);
  }

  private filterTaxonomies(searchTerm: string): ProviderTaxonomy[] {
    if (!searchTerm) {
      return this.taxonomies;
    }

    return this.taxonomies.filter(
      (taxonomy) =>
        taxonomy.providerTaxonomyDescription
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        taxonomy.providerTaxonomyCode
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase())
    );
  }

  shouldShowActiveCheckbox(): boolean {
    return this.isEdit && this.data?.provider?.active === 0;
  }

  isNPIValidating(): boolean {
    const npiControl = this.providerForm.get("npi");
    return npiControl?.pending ?? false;
  }

  isSaveDisabled(): boolean {
    return (
      this.providerForm.invalid ||
      this.hasSaveButtonBeenPressed ||
      this.isNPIValidating() ||
      this.npiLookupStatus() === 'not-found' ||
      this.npiLookupStatus() === 'loading'
    );
  }
}